/** @format */

import {
	Box,
	Button,
	Card,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	Grid,
	InputAdornment,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Typography,
	IconButton,
	Tooltip,
	CircularProgress,
	Avatar,
} from "@mui/material";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "./AllUsers.css";
import OutlinedInput from "@mui/material/OutlinedInput";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/ssr/MagnifyingGlass";
import { Trash } from "@phosphor-icons/react/dist/ssr/Trash";
import { ArrowsClockwise } from "@phosphor-icons/react/dist/ssr/ArrowsClockwise";
import BlockIcon from "@mui/icons-material/Block";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import GroupsIcon from "@mui/icons-material/Groups";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store/store";
import {
	getAllUsersAsAdmin,
	banUser,
	unbanUser,
	deleteUser,
	updateUserByIdAsAdmin,
} from "../../../redux/actions/userActions";
import {
	selectAllUsersAsAdmin,
	selectAllUsersLoading,
} from "../../../redux/selectors/userSelector";
import {
	LoadingSpinner,
	SuccessNotification,
} from "../../allComponents/AllComponents";
import UserProfileHeader from "../../userprofile-header/UserProfileHeader";
import DrawerNavbar from "../../drawerNavBar/DrawerNavbar";
import Main from "../../../utility/common/Main";
import {
	UserSummary,
	IPhoneContact,
	EmptyPhoneContact,
} from "../../../utility/types/userType";

const AllUsers = () => {
	const dispatch = useDispatch<AppDispatch>();
	const allUsersData = useSelector(selectAllUsersAsAdmin);
	const isLoading = useSelector(selectAllUsersLoading);
	const [openNav, setOpenNav] = useState(false);
	const [processingUserId, setProcessingUserId] = useState<string | null>(null);
	const [isProcessing, setIsProcessing] = useState(false);

	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [searchQuery, setSearchQuery] = useState("");
	const [confirmDialog, setConfirmDialog] = useState<{
		open: boolean;
		type: "ban" | "unban" | "delete" | null;
		userId: string | null;
		userName: string;
	}>({
		open: false,
		type: null,
		userId: null,
		userName: "",
	});
	const [detailsModal, setDetailsModal] = useState<{
		open: boolean;
		user: UserSummary | null;
		isEditing: boolean;
	}>({
		open: false,
		user: null,
		isEditing: false,
	});
	const [editedUser, setEditedUser] = useState<Partial<UserSummary>>({});
	const [notification, setNotification] = useState({
		open: false,
		message: "",
		severity: "success" as "success" | "error" | "info" | "warning",
	});
	const [circlesPage, setCirclesPage] = useState(0);
	const [circlesRowsPerPage, setCirclesRowsPerPage] = useState(4);

	useEffect(() => {
		dispatch(getAllUsersAsAdmin());
	}, [dispatch]);

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(event.target.value);
		setPage(0); // Reset to first page when search changes
	};

	const handleRefresh = async () => {
		try {
			await dispatch(getAllUsersAsAdmin()).unwrap();
			setNotification({
				open: true,
				message: "Users refreshed successfully",
				severity: "success",
			});
		} catch (err: any) {
			setNotification({
				open: true,
				message: err?.message || err?.toString() || "Failed to refresh users",
				severity: "error",
			});
		}
	};

	const handleOpenConfirmDialog = (
		type: "ban" | "unban" | "delete",
		userId: string,
		userName: string,
	) => {
		setConfirmDialog({
			open: true,
			type,
			userId,
			userName,
		});
	};

	const handleCloseConfirmDialog = () => {
		if (!isProcessing) {
			setConfirmDialog({
				open: false,
				type: null,
				userId: null,
				userName: "",
			});
		}
	};

	const handleConfirmAction = async () => {
		if (!confirmDialog.userId || !confirmDialog.type) return;

		setIsProcessing(true);
		setProcessingUserId(confirmDialog.userId);

		try {
			if (confirmDialog.type === "ban") {
				await dispatch(
					banUser({
						userId: confirmDialog.userId,
						reason: "Banned by admin",
					}),
				).unwrap();
			} else if (confirmDialog.type === "unban") {
				await dispatch(unbanUser(confirmDialog.userId)).unwrap();
			} else if (confirmDialog.type === "delete") {
				await dispatch(
					deleteUser({
						userId: confirmDialog.userId,
						reason: "Deleted by admin",
					}),
				).unwrap();
			}

			setNotification({
				open: true,
				message: `User ${
					confirmDialog.type === "ban" ? "banned"
					: confirmDialog.type === "unban" ? "unbanned"
					: "deleted"
				} successfully`,
				severity: "success",
			});

			handleCloseConfirmDialog();
			await dispatch(getAllUsersAsAdmin()).unwrap();
		} catch (err: any) {
			setNotification({
				open: true,
				message: err?.message || `Failed to ${confirmDialog.type} user`,
				severity: "error",
			});
		} finally {
			setIsProcessing(false);
			setProcessingUserId(null);
		}
	};

	const handleOpenDetailsModal = (user: UserSummary) => {
		setDetailsModal({
			open: true,
			user,
			isEditing: false,
		});
		// Initialize phone with EmptyPhoneContact if null to prevent undefined errors
		setEditedUser({
			...user,
			phone: user.phone ?? EmptyPhoneContact,
		});
		setCirclesPage(0); // Reset circles pagination when opening modal
	};

	const handleCloseDetailsModal = () => {
		setDetailsModal({
			open: false,
			user: null,
			isEditing: false,
		});
		setEditedUser({});
		setCirclesPage(0); // Reset circles pagination when closing modal
	};

	const handleToggleEdit = () => {
		setDetailsModal((prev) => ({
			...prev,
			isEditing: !prev.isEditing,
		}));
	};

	const handleCirclesPageChange = (
		event: React.MouseEvent<HTMLButtonElement> | null,
		newPage: number,
	) => {
		setCirclesPage(newPage);
	};

	const handleCirclesRowsPerPageChange = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		setCirclesRowsPerPage(parseInt(event.target.value, 10));
		setCirclesPage(0);
	};

	const handleSaveUser = async () => {
		if (!detailsModal.user) return;

		setIsProcessing(true);
		try {
			// Create update data object
			const updateData: any = {};
			if (editedUser.firstName) updateData.firstName = editedUser.firstName;
			if (editedUser.lastName) updateData.lastName = editedUser.lastName;
			if (editedUser.email) updateData.email = editedUser.email;
			if (editedUser.phone) updateData.phone = editedUser.phone;

			await dispatch(
				updateUserByIdAsAdmin({
					userId: detailsModal.user.userId,
					updateData,
				}),
			).unwrap();

			setNotification({
				open: true,
				message: "User updated successfully",
				severity: "success",
			});

			handleCloseDetailsModal();
			await dispatch(getAllUsersAsAdmin()).unwrap();
		} catch (err: any) {
			setNotification({
				open: true,
				message: err?.message || "Failed to update user",
				severity: "error",
			});
		} finally {
			setIsProcessing(false);
		}
	};

	const handleEditChange = (field: keyof UserSummary, value: any) => {
		if (field === "phone") {
			// Ensure phone object exists before updating e164
			setEditedUser((prev) => ({
				...prev,
				phone: {
					...(prev.phone || EmptyPhoneContact),
					e164: value || "",
				} as IPhoneContact,
			}));
		} else {
			setEditedUser((prev) => ({
				...prev,
				[field]: value,
			}));
		}
	};

	const handleChangePage = (_event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const users = allUsersData?.users || [];
	const totalUsers = allUsersData?.totalUsers || 0;

	const filteredUsers = users.filter((user: any) => {
		const searchLower = searchQuery.toLowerCase();
		const firstNameMatch = user.firstName?.toLowerCase().includes(searchLower);
		const lastNameMatch = user.lastName?.toLowerCase().includes(searchLower);
		const emailMatch = user.email?.toLowerCase().includes(searchLower);
		return firstNameMatch || lastNameMatch || emailMatch;
	});

	const paginatedUsers = filteredUsers.slice(
		page * rowsPerPage,
		page * rowsPerPage + rowsPerPage,
	);

	const getUserStatusColor = (
		user: UserSummary,
	): "default" | "success" | "error" | "warning" => {
		if (user.isDeleted) return "error";
		if (user.isBanned) return "error";
		if (
			allUsersData?.users.map(
				(u: any) =>
					u.isVerified === true &&
					u.isBanned === false &&
					u.isDeleted === false,
			)
		)
			return "success";
		return "warning";
	};

	const getUserStatusLabel = (user: UserSummary): string => {
		if (user.isDeleted) return "Deleted";
		if (user.isBanned) return "Banned";

		if (
			allUsersData?.users.map(
				(u) =>
					u.isVerified === true &&
					u.isBanned === false &&
					u.isDeleted === false,
			)
		) {
			return "Active";
		}
		if (user.isDeleted) return "Deleted";
		if (user.isBanned) return "Banned";

		return "Inactive";
	};

	const PhoneTextField = React.forwardRef<HTMLInputElement, any>(
		function PhoneTextField(props, ref) {
			const { value, onChange, ...rest } = props;

			return (
				<TextField
					{...rest}
					inputRef={ref}
					value={value ?? ""}
					onChange={(e) => onChange(e.target.value)}
				/>
			);
		},
	);

	return (
		<React.Fragment>
			<UserProfileHeader onOpenNav={() => setOpenNav(true)} />
			<Box
				sx={{
					minHeight: 1,
					display: "flex",
					flexDirection: { xs: "column", lg: "row" },
				}}>
				<DrawerNavbar openNav={openNav} onCloseNav={() => setOpenNav(false)} />

				<Main>
					{isLoading ?
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								minHeight: "60vh",
							}}>
							<LoadingSpinner loading={isLoading} size={45} />
						</Box>
					:	<>
							<Stack spacing={3}>
								<Stack direction="row" spacing={3}>
									<Stack spacing={1} sx={{ flex: "1 1 auto" }}>
										<Typography variant="h4">All Users</Typography>
										<Typography color="text.secondary" variant="body2">
											Manage all platform users
										</Typography>
									</Stack>
									<Box sx={{ display: "flex", alignItems: "center" }}>
										<Tooltip title="Refresh">
											<IconButton onClick={handleRefresh} disabled={isLoading}>
												<ArrowsClockwise />
											</IconButton>
										</Tooltip>
									</Box>
								</Stack>

								{/* Stats Cards */}
								<Stack direction="row" spacing={2}>
									<Card sx={{ flex: 1, p: 2 }}>
										<Typography variant="h6" color="primary">
											{totalUsers}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Total Users
										</Typography>
									</Card>
									<Card sx={{ flex: 1, p: 2 }}>
										<Typography variant="h6" color="success.main">
											{
												users.filter(
													(u) => u.isVerified && !u.isBanned && !u.isDeleted,
												).length
											}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Active
										</Typography>
									</Card>
									<Card sx={{ flex: 1, p: 2 }}>
										<Typography variant="h6" color="error.main">
											{users.filter((u) => u.isBanned).length}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Banned
										</Typography>
									</Card>
									<Card sx={{ flex: 1, p: 2 }}>
										<Typography variant="h6" color="warning.main">
											{users.filter((u) => u.isDeleted).length}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Deleted
										</Typography>
									</Card>
								</Stack>

								{/* Search */}
								<Card>
									<Stack
										direction="row"
										spacing={2}
										sx={{ p: 2 }}
										alignItems="center">
										<OutlinedInput
											value={searchQuery}
											onChange={handleSearchChange}
											fullWidth
											placeholder="Search by name or email..."
											startAdornment={
												<InputAdornment position="start">
													<MagnifyingGlassIcon fontSize="var(--icon-fontSize-md)" />
												</InputAdornment>
											}
										/>
									</Stack>
								</Card>
								<Card>
									{isLoading ?
										<Box
											sx={{
												display: "flex",
												justifyContent: "center",
												alignItems: "center",
												minHeight: 400,
											}}>
											<CircularProgress />
										</Box>
									:	<>
											<Box sx={{ overflowX: "auto" }}>
												<Table sx={{ minWidth: "800px" }}>
													<TableHead>
														<TableRow>
															<TableCell>User</TableCell>
															<TableCell>Email</TableCell>
															<TableCell>Status</TableCell>
															<TableCell>Circles</TableCell>
															<TableCell>Verified</TableCell>
															<TableCell>Joined</TableCell>
															<TableCell align="center">More Info</TableCell>
															<TableCell align="center">Actions</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{paginatedUsers.length === 0 ?
															<TableRow>
																<TableCell colSpan={8} align="center">
																	<Typography
																		variant="body2"
																		color="text.secondary">
																		No users found
																	</Typography>
																</TableCell>
															</TableRow>
														:	paginatedUsers.map((user: UserSummary) => (
																<TableRow hover key={user.userId}>
																	<TableCell>
																		<Stack
																			direction="row"
																			spacing={2}
																			alignItems="center">
																			<Avatar
																				src={user.profileImage}
																				sx={{ width: 40, height: 40 }}>
																				{`${user.firstName?.[0]}${user.lastName?.[0]}`}
																			</Avatar>
																			<Typography variant="subtitle2">
																				{`${user.firstName} ${user.lastName}`}
																			</Typography>
																		</Stack>
																	</TableCell>
																	<TableCell>{user.email}</TableCell>
																	<TableCell>
																		<Chip
																			label={getUserStatusLabel(user)}
																			color={getUserStatusColor(user)}
																			size="small"
																		/>
																	</TableCell>
																	<TableCell>{user.circleCount || 0}</TableCell>
																	<TableCell>
																		<Chip
																			label={user.isVerified ? "Yes" : "No"}
																			color={
																				user.isVerified ? "success" : "warning"
																			}
																			size="small"
																		/>
																	</TableCell>
																	<TableCell>
																		{user.createdAt ?
																			dayjs(user.createdAt).format(
																				"MMM D, YYYY",
																			)
																		:	"N/A"}
																	</TableCell>
																	<TableCell align="center">
																		<Tooltip title="More Info">
																			<IconButton
																				color="primary"
																				size="small"
																				onClick={() =>
																					handleOpenDetailsModal(user)
																				}>
																				<InfoIcon />
																			</IconButton>
																		</Tooltip>
																	</TableCell>
																	<TableCell>
																		<Stack
																			direction="row"
																			spacing={1}
																			justifyContent="center">
																			{!user.isBanned && !user.isDeleted && (
																				<Tooltip title="Ban User">
																					<IconButton
																						color="warning"
																						size="small"
																						disabled={
																							isProcessing &&
																							processingUserId === user.userId
																						}
																						onClick={() =>
																							handleOpenConfirmDialog(
																								"ban",
																								user.userId,
																								`${user.firstName} ${user.lastName}`,
																							)
																						}>
																						{(
																							isProcessing &&
																							processingUserId === user.userId
																						) ?
																							<CircularProgress size={20} />
																						:	<BlockIcon />}
																					</IconButton>
																				</Tooltip>
																			)}
																			{user.isBanned && (
																				<Tooltip title="Unban User">
																					<IconButton
																						color="success"
																						size="small"
																						disabled={
																							isProcessing &&
																							processingUserId === user.userId
																						}
																						onClick={() =>
																							handleOpenConfirmDialog(
																								"unban",
																								user.userId,
																								`${user.firstName} ${user.lastName}`,
																							)
																						}>
																						{(
																							isProcessing &&
																							processingUserId === user.userId
																						) ?
																							<CircularProgress size={20} />
																						:	<BlockIcon />}
																					</IconButton>
																				</Tooltip>
																			)}
																			{!user.isDeleted && (
																				<Tooltip title="Delete User">
																					<IconButton
																						color="error"
																						size="small"
																						disabled={
																							isProcessing &&
																							processingUserId === user.userId
																						}
																						onClick={() =>
																							handleOpenConfirmDialog(
																								"delete",
																								user.userId,
																								`${user.firstName} ${user.lastName}`,
																							)
																						}>
																						{(
																							isProcessing &&
																							processingUserId === user.userId
																						) ?
																							<CircularProgress size={20} />
																						:	<Trash />}
																					</IconButton>
																				</Tooltip>
																			)}
																		</Stack>
																	</TableCell>
																</TableRow>
															))
														}
													</TableBody>
												</Table>
											</Box>
											<Divider />
											<TablePagination
												component="div"
												count={filteredUsers.length}
												onPageChange={handleChangePage}
												onRowsPerPageChange={handleChangeRowsPerPage}
												page={page}
												rowsPerPage={rowsPerPage}
												rowsPerPageOptions={[5, 10, 25, 50]}
											/>
										</>
									}
								</Card>

								{/* Confirmation Dialog */}
								<Dialog
									open={confirmDialog.open}
									onClose={!isProcessing ? handleCloseConfirmDialog : undefined}
									aria-labelledby="confirm-dialog-title">
									<DialogTitle id="confirm-dialog-title">
										{confirmDialog.type === "ban" && "Ban User"}
										{confirmDialog.type === "unban" && "Unban User"}
										{confirmDialog.type === "delete" && "Delete User"}
									</DialogTitle>
									<DialogContent>
										{isProcessing ?
											<Box
												sx={{
													display: "flex",
													flexDirection: "column",
													alignItems: "center",
													gap: 2,
													py: 3,
												}}>
												<CircularProgress size={40} />
												<Typography variant="body2" color="text.secondary">
													Processing...
												</Typography>
											</Box>
										:	<DialogContentText>
												Are you sure you want to{" "}
												{confirmDialog.type === "ban" ?
													"ban"
												: confirmDialog.type === "unban" ?
													"unban"
												:	"delete"}{" "}
												<strong>{confirmDialog.userName}</strong>?
												{confirmDialog.type === "delete" &&
													" This action will soft-delete the user account."}
												{confirmDialog.type === "ban" &&
													" This will prevent the user from accessing the platform."}
												{confirmDialog.type === "unban" &&
													" This will restore the user's access to the platform."}
											</DialogContentText>
										}
									</DialogContent>
									<DialogActions>
										<Button
											onClick={handleCloseConfirmDialog}
											disabled={isProcessing}>
											Cancel
										</Button>
										<Button
											onClick={handleConfirmAction}
											color={
												confirmDialog.type === "ban" ? "warning"
												: confirmDialog.type === "unban" ?
													"success"
												:	"error"
											}
											variant="contained"
											disabled={isProcessing}>
											{confirmDialog.type === "ban" ?
												"Ban"
											: confirmDialog.type === "unban" ?
												"Unban"
											:	"Delete"}
										</Button>
									</DialogActions>
								</Dialog>

								{/* User Details Modal */}
								<Dialog
									open={detailsModal.open}
									onClose={handleCloseDetailsModal}
									maxWidth="md"
									fullWidth
									aria-labelledby="details-dialog-title"
									PaperProps={{
										sx: {
											borderRadius: 3,
											overflow: "hidden",
										},
									}}>
									<DialogTitle
										id="details-dialog-title"
										sx={{
											background:
												"linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
											color: "white",
											py: 2.5,
										}}>
										<Stack
											direction="row"
											justifyContent="space-between"
											alignItems="center">
											<Typography variant="h5" fontWeight={600}>
												User Details
											</Typography>
											<Stack direction="row" spacing={1}>
												{detailsModal.isEditing ?
													<>
														<IconButton
															size="small"
															sx={{ color: "white" }}
															onClick={handleSaveUser}
															disabled={isProcessing}>
															{isProcessing ?
																<CircularProgress
																	size={20}
																	sx={{ color: "white" }}
																/>
															:	<SaveIcon />}
														</IconButton>
														<IconButton
															size="small"
															sx={{ color: "white" }}
															onClick={handleToggleEdit}
															disabled={isProcessing}>
															<CloseIcon />
														</IconButton>
													</>
												:	<IconButton
														size="small"
														sx={{ color: "white" }}
														onClick={handleToggleEdit}>
														<EditIcon />
													</IconButton>
												}
											</Stack>
										</Stack>
									</DialogTitle>
									<DialogContent sx={{ p: 0 }}>
										{detailsModal.user && (
											<Box>
												{/* User Profile Header */}
												<Box
													sx={{
														background:
															"linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
														pt: 0,
														pb: 8,
														px: 3,
													}}>
													<Stack
														direction="row"
														spacing={3}
														alignItems="center">
														<Avatar
															src={detailsModal.user.profileImage}
															sx={{
																width: 80,
																height: 80,
																border: "4px solid white",
																boxShadow: 3,
															}}>
															{`${detailsModal.user.firstName?.[0]}${detailsModal.user.lastName?.[0]}`}
														</Avatar>
														<Box>
															<Typography
																variant="h5"
																fontWeight={600}
																color="white">
																{detailsModal.isEditing ?
																	<TextField
																		size="small"
																		value={editedUser.firstName || ""}
																		onChange={(e) =>
																			handleEditChange(
																				"firstName",
																				e.target.value,
																			)
																		}
																		sx={{
																			mr: 1,
																			"& .MuiInputBase-input": {
																				color: "white",
																			},
																			"& .MuiOutlinedInput-notchedOutline": {
																				borderColor: "rgba(255,255,255,0.5)",
																			},
																			"&:hover .MuiOutlinedInput-notchedOutline":
																				{
																					borderColor: "white",
																				},
																		}}
																		placeholder="First Name"
																	/>
																:	detailsModal.user.firstName}{" "}
																{detailsModal.isEditing ?
																	<TextField
																		size="small"
																		value={editedUser.lastName || ""}
																		onChange={(e) =>
																			handleEditChange(
																				"lastName",
																				e.target.value,
																			)
																		}
																		sx={{
																			"& .MuiInputBase-input": {
																				color: "white",
																			},
																			"& .MuiOutlinedInput-notchedOutline": {
																				borderColor: "rgba(255,255,255,0.5)",
																			},
																			"&:hover .MuiOutlinedInput-notchedOutline":
																				{
																					borderColor: "white",
																				},
																		}}
																		placeholder="Last Name"
																	/>
																:	detailsModal.user.lastName}
															</Typography>
															<Typography
																variant="body2"
																sx={{
																	color: "rgba(255,255,255,0.85)",
																	mt: 0.5,
																}}>
																{detailsModal.isEditing ?
																	<TextField
																		size="small"
																		fullWidth
																		value={editedUser.email || ""}
																		onChange={(e) =>
																			handleEditChange("email", e.target.value)
																		}
																		sx={{
																			mt: 0.5,
																			"& .MuiInputBase-input": {
																				color: "white",
																				fontSize: "0.875rem",
																			},
																			"& .MuiOutlinedInput-notchedOutline": {
																				borderColor: "rgba(255,255,255,0.5)",
																			},
																			"&:hover .MuiOutlinedInput-notchedOutline":
																				{
																					borderColor: "white",
																				},
																		}}
																		placeholder="Email"
																	/>
																:	detailsModal.user.email}
															</Typography>
															{detailsModal.user.phone?.e164 && (
																<Typography
																	variant="body2"
																	sx={{ color: "rgba(255,255,255,0.75)" }}>
																	{detailsModal.user.phone.e164}
																</Typography>
															)}
														</Box>
													</Stack>
												</Box>

												{/* Status Cards - Overlapping Design */}
												<Box sx={{ px: 3, mt: -5 }}>
													<Grid container spacing={2}>
														<Grid item xs={12} sm={4}>
															<Card
																sx={{
																	p: 2.5,
																	borderRadius: 2,
																	boxShadow: 3,
																	textAlign: "center",
																	background:
																		detailsModal.user.isBanned ?
																			"linear-gradient(135deg, #eb3349 0%, #f45c43 100%)"
																		:	"linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
																	color: "white",
																}}>
																<VerifiedUserIcon
																	sx={{ fontSize: 32, mb: 1 }}
																/>
																<Typography
																	variant="body2"
																	sx={{ opacity: 0.9 }}>
																	Account Status
																</Typography>
																<Typography variant="h6" fontWeight={700}>
																	{detailsModal.user.isBanned ?
																		"Banned"
																	:	"Active"}
																</Typography>
															</Card>
														</Grid>
														<Grid item xs={12} sm={4}>
															<Card
																sx={{
																	p: 2.5,
																	borderRadius: 2,
																	boxShadow: 3,
																	textAlign: "center",
																	background:
																		(
																			detailsModal.user.phone?.consent
																				?.status === "opted_in"
																		) ?
																			"linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
																		:	"linear-gradient(135deg, #eb3349 0%, #f45c43 100%)",
																	color: "white",
																}}>
																<PhoneIphoneIcon sx={{ fontSize: 32, mb: 1 }} />
																<Typography
																	variant="body2"
																	sx={{ opacity: 0.9 }}>
																	SMS Consent
																</Typography>
																<Typography variant="h6" fontWeight={700}>
																	{(
																		detailsModal.user.phone?.consent?.status ===
																		"opted_in"
																	) ?
																		"Opted In"
																	:	detailsModal.user.phone?.consent?.status?.toUpperCase() ||
																		"Unknown"
																	}
																</Typography>
															</Card>
														</Grid>
														<Grid item xs={12} sm={4}>
															<Card
																sx={{
																	p: 2.5,
																	borderRadius: 2,
																	boxShadow: 3,
																	textAlign: "center",
																	background:
																		"linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
																	color: "white",
																}}>
																<GroupsIcon sx={{ fontSize: 32, mb: 1 }} />
																<Typography
																	variant="body2"
																	sx={{ opacity: 0.9 }}>
																	Total Circles
																</Typography>
																<Typography variant="h6" fontWeight={700}>
																	{detailsModal.user.circles?.length || 0}
																</Typography>
															</Card>
														</Grid>
													</Grid>
												</Box>

												{/* Account Information Section */}
												<Box sx={{ px: 3, py: 3 }}>
													<Typography
														variant="subtitle1"
														fontWeight={600}
														sx={{ mb: 2, color: "text.secondary" }}>
														Account Information
													</Typography>
													<Card
														variant="outlined"
														sx={{ borderRadius: 2, overflow: "hidden" }}>
														<Stack divider={<Divider />}>
															<Stack
																direction="row"
																justifyContent="space-between"
																alignItems="center"
																sx={{ px: 2.5, py: 1.5 }}>
																<Typography
																	variant="body2"
																	color="text.secondary">
																	User ID
																</Typography>
																<Typography
																	variant="body2"
																	fontWeight={500}
																	sx={{ fontFamily: "monospace" }}>
																	{detailsModal.user.userId}
																</Typography>
															</Stack>
															<Stack
																direction="row"
																justifyContent="space-between"
																alignItems="center"
																sx={{ px: 2.5, py: 1.5 }}>
																<Typography
																	variant="body2"
																	color="text.secondary">
																	Phone Number
																</Typography>
																{detailsModal.isEditing ?
																	<PhoneInput
																		defaultCountry="US"
																		international
																		value={editedUser.phone?.e164 ?? undefined}
																		onChange={(value) =>
																			handleEditChange("phone", value || "")
																		}
																		className="admin-phone-input-container"
																		style={{ width: "auto" }}
																		inputComponent={PhoneTextField}
																		inputProps={{
																			size: "small",
																			variant: "outlined",
																		}}
																	/>
																:	<Typography variant="body1" fontWeight={500}>
																		{detailsModal.user.phone?.e164 || "Not Set"}
																	</Typography>
																}
															</Stack>
															<Stack
																direction="row"
																justifyContent="space-between"
																alignItems="center"
																sx={{ px: 2.5, py: 1.5 }}>
																<Typography
																	variant="body2"
																	color="text.secondary">
																	Account Status
																</Typography>
																<Chip
																	label={
																		detailsModal.user.isBanned ?
																			"Banned"
																		:	"Active"
																	}
																	color={
																		detailsModal.user.isBanned ?
																			"error"
																		:	"success"
																	}
																	size="small"
																/>
															</Stack>
															<Stack
																direction="row"
																justifyContent="space-between"
																alignItems="center"
																sx={{ px: 2.5, py: 1.5 }}>
																<Typography
																	variant="body2"
																	color="text.secondary">
																	SMS Consent Status
																</Typography>
																<Chip
																	label={
																		detailsModal.user.phone?.consent?.status?.toUpperCase() ||
																		"UNKNOWN"
																	}
																	color={
																		(
																			detailsModal.user.phone?.consent
																				?.status === "opted_in"
																		) ?
																			"success"
																		:	"default"
																	}
																	size="small"
																/>
															</Stack>
														</Stack>
													</Card>
												</Box>

												{/* Circles Section */}
												{detailsModal.user.circles &&
													detailsModal.user.circles.length > 0 && (
														<Box sx={{ px: 3, pb: 3 }}>
															<Typography
																variant="subtitle1"
																fontWeight={600}
																sx={{ mb: 2, color: "text.secondary" }}>
																Circle Memberships (
																{detailsModal.user.circles.length})
															</Typography>
															<Stack spacing={2}>
																{detailsModal.user.circles
																	.slice(
																		circlesPage * circlesRowsPerPage,
																		circlesPage * circlesRowsPerPage +
																			circlesRowsPerPage,
																	)
																	.map((circle) => (
																		<Card
																			key={circle._id}
																			variant="outlined"
																			sx={{
																				borderRadius: 2,
																				transition: "all 0.2s ease-in-out",
																				"&:hover": {
																					boxShadow: 2,
																					borderColor: "primary.main",
																				},
																			}}>
																			<Stack
																				direction="row"
																				spacing={2}
																				alignItems="center"
																				sx={{ p: 2 }}>
																				<Avatar
																					alt={circle.circleName}
																					src={circle.circleImage}
																					sx={{
																						width: 56,
																						height: 56,
																						borderRadius: 2,
																					}}
																					variant="rounded"
																				/>
																				<Box sx={{ flex: 1, minWidth: 0 }}>
																					<Stack
																						direction="row"
																						alignItems="center"
																						spacing={1}>
																						<Typography
																							variant="subtitle1"
																							fontWeight={600}
																							noWrap>
																							{circle.circleName}
																						</Typography>
																						{circle.isLeader && (
																							<Tooltip title="Circle Leader">
																								<Chip
																									icon={
																										<StarIcon
																											sx={{
																												color:
																													"gold !important",
																											}}
																										/>
																									}
																									label="Leader"
																									size="small"
																									sx={{
																										bgcolor: "warning.lighter",
																										color: "warning.dark",
																									}}
																								/>
																							</Tooltip>
																						)}
																					</Stack>
																				</Box>
																			</Stack>
																		</Card>
																	))}
															</Stack>
															{detailsModal.user.circles.length >
																circlesRowsPerPage && (
																<TablePagination
																	page={circlesPage}
																	component="div"
																	count={detailsModal.user.circles.length}
																	rowsPerPage={circlesRowsPerPage}
																	onPageChange={handleCirclesPageChange}
																	rowsPerPageOptions={[4, 8, 12]}
																	onRowsPerPageChange={
																		handleCirclesRowsPerPageChange
																	}
																	sx={{ mt: 2 }}
																/>
															)}
														</Box>
													)}
											</Box>
										)}
									</DialogContent>
									<DialogActions sx={{ px: 3, py: 2 }}>
										<Button
											onClick={handleCloseDetailsModal}
											variant="outlined"
											sx={{ borderRadius: 2, px: 3 }}>
											Close
										</Button>
									</DialogActions>
								</Dialog>

								{/* Success/Error Notification */}
								<SuccessNotification
									open={notification.open}
									onClose={() =>
										setNotification((prev) => ({ ...prev, open: false }))
									}
									message={notification.message}
									severity={notification.severity}
								/>
							</Stack>
						</>
					}
				</Main>
			</Box>
		</React.Fragment>
	);
};

export default AllUsers;
